<template>
  <div class="legal-search-home">
    <!-- ================= 首页 ================= -->
    <template v-if="searched === 1">
      <!-- 页面标题 -->
      <div class="header-home">
        <h1>AI 法律检索</h1>

        <p>
          围绕案件事实、争议焦点、裁判观点等内容，智能检索相关案例及法律法规
        </p>
      </div>

      <!-- 搜索 -->
      <SearchInput
        v-model="keyword"
        @search="handleSearch"
        @upload="handleUpload"
        @optimize="handleOptimize"
      />
      <!-- 上传弹窗组件 -->
      <UploadFileModal
        v-model:open="modalOpen"
        @upload-success="handleUploadFile"
      />
      <div class="icons-title">
        <img src="@/assets/casesearchImg/icon-recognition.png" class="icons" />
        小贴士：补充案件事实、争议焦点、时间地点等信息，可提升类案匹配准确度。
      </div>

      <!-- 推荐案例 -->
      <div class="recommend-section">
        <div class="recommend-header">
          <div class="left">
            <h2>推荐检索示例</h2>
          </div>

          <a-button type="link" @click="changeRecommend"> 换一批 </a-button>
        </div>

        <RecommendList :list="recommendList" @detail="goDetail" />
      </div>

      <!-- 统计 -->
      <Statistics :list="statistics" />
    </template>
    <!-- 等待页面 -->
    <template v-else-if="searched === 2">
      <div class="main-right-content">
        <!-- 加载组件，全部步骤走完触发finish事件 -->
        <SearchLoadingPanel
          ref="loadingPanelRef"
          :keyword="keyword"
          @back="searched = 1"
          @finish="onThinkFinished"
        />
      </div>
    </template>

    <!-- ================= 检索结果 ================= -->
    <SearchWorkbench
      ref="workbenchRef"
      v-show="searched === 3"
      :keyword="keyword"
      :year="year"
      :courtName="courtName"
      :caseListData="caseListData"
      :thinkHtml="thinkHtml"
      :thinkStreaming="thinkStreaming"
      :rawThinkText="rawThinkText"
      :groupId="groupId"
      @back="searched = 1"
      @detail="goDetails"
      @goView="goView"
      @viewDetail="goDetails"
      @favoriteChange="onListFavoriteChange"
    />

    <CaseDetail
      v-show="searched === 4"
      :analyzeData="analyzeDetailData"
      :isFavorited="currentDetailFavorited"
      @back="searched = 3"
      @favoriteChange="onDetailFavoriteChange"
    />
  </div>

  <!-- 全局加载遮罩 -->
  <div v-if="loading" class="loading-overlay">
    <div class="loading-card">
      <a-spin size="large" />
      <div class="loading-text">加载中...</div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, watch, inject } from "vue";

import SearchInput from "./SearchInput.vue";
import RecommendList from "./RecommendList.vue";
import Statistics from "./SearchStatistics.vue";
import SearchWorkbench from "./SearchWorkbench.vue";
import CaseDetail from "./CaseDetail.vue";
import UploadFileModal from "./UploadFileModal.vue";
import SearchLoadingPanel from "./SearchLoadingPanel.vue";
import {
  GetRandonList,
  HybridSearch,
  AnalyzeCase,
  GenerateFollowUpQuestions,
} from "../../api/case/case";
import { message } from "ant-design-vue";
import {
  // recommendListData,
  // caseListData,
  statisticsData,
} from "./mock";
const groupId = ref("");
const keyword = ref("");
const year = ref(0);
const courtName = ref("全部法院");

const searched = ref(1);

// kevinHome 提供的侧边栏收起方法：进入结果页（报告生成/查看）时自动收起，
// 路由不变只能靠页面状态触发；与路由规则同语义，只收起不展开
const collapseSider = inject("collapseSider", () => {});
watch(searched, (val) => {
  if (val === 3) collapseSider();
});

// 取消标记：返回首页时设为 true，下一个请求开始前重置
let searchCancelled = false;

const recommendList = ref();

const statistics = ref(statisticsData);

const caseListData = ref([]);
const dailyCaseSearchLimited = ref(false);
const currentAH = ref(""); // 当前详情页的案号
const currentDetailFavorited = ref(false); // 当前详情页的收藏状态

// 详情页收藏状态变更
const onDetailFavoriteChange = (favorited) => {
  currentDetailFavorited.value = favorited;
  // 同步更新 caseListData 中对应项的 isFavorited
  const item = caseListData.value.find((i) => i.ah === currentAH.value);
  if (item) {
    item.isFavorited = favorited;
    // 触发数组引用更新，让下游组件（CaseListPanel）的 watch 感知变化
    caseListData.value = [...caseListData.value];
  }
};

// 列表侧收藏状态变更：就地更新源头数据即可，不要整体重赋值——
// 列表本地已即时更新；重赋值会触发 CaseListPanel 的 watch 重新深拷贝，抹掉勾选等本地状态。
// 源头正确后，下次新搜索等导致的重灌也不会丢收藏状态
const onListFavoriteChange = ({ ahs, favorited }) => {
  ahs.forEach((ah) => {
    const item = caseListData.value.find((i) => i.ah === ah);
    if (item) item.isFavorited = favorited;
  });
};

const loading = ref(false);

const analyzeDetailData = ref(null);

const workbenchRef = ref();
const loadingPanelRef = ref();
const thinkHtml = ref("");
const thinkStreaming = ref(false);
const rawThinkText = ref("");

// 两个条件都满足后才切换页面
let hybridSearchFinished = false;
let thinkStreamFinished = false;
let previousKeyword = "";

const checkBothFinished = () => {
  if (hybridSearchFinished && thinkStreamFinished) {
    previousKeyword = keyword.value;
    handleLoadFinish();
  }
};

// 从共享数据恢复（来自历史记录跳转等）
const restoreFromCrossData = async (detail) => {
  try {
    // detail 可能是数组，取第一个有 requestParams 的对象（类案检索）
    const searchRecord = Array.isArray(detail)
      ? detail.find((d) => d.requestParams)
      : detail;
    if (!searchRecord) {
      console.error("从共享数据恢复失败：没有有效的记录", detail);
      return;
    }

    // 解析请求参数
    const params = JSON.parse(searchRecord.requestParams);
    keyword.value = params.request.query || "";
    year.value = params.request.year || 0;
    courtName.value = params.request.courtName || "全部法院";

    // 获取第二条数据（检索报告/案例分析报告）
    const reportRecord =
      Array.isArray(detail) && detail.length > 1 ? detail[1] : null;

    // 检查类案检索的 responseResult 是否为空
    const searchResultEmpty = !searchRecord.responseResult;

    // 检查案例分析报告的 responseResult 是否为空
    const reportResultEmpty = !reportRecord || !reportRecord.responseResult;

    // 1. 如果类案检索结果为空，走完整等待流程（HybridSearch + CaseThink + GenerateFollowUpQuestions）
    if (searchResultEmpty) {
      // 清除预置 md 缓存
      window.__preloadMd = null;

      // 重置完成状态
      hybridSearchFinished = false;
      thinkStreamFinished = false;

      searched.value = 2;

      // 并行调用 GenerateFollowUpQuestions
      GenerateFollowUpQuestions(keyword.value)
        .then((res) => {
          if (res.isSuccess && res.data?.questions) {
            window.__pendingFollowUpQuestions = res.data.questions;
            workbenchRef.value?.workbenchRightRef?.setRecommendQuestions?.(
              res.data.questions,
            );
          }
        })
        .catch((err) => {
          console.error("推荐问题获取失败", err);
        });

      try {
        groupId.value = `${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 10)}-${Math.random().toString(36).slice(2, 6)}`;
        const res = await HybridSearch({
          query: keyword.value,
          courtName: courtName.value,
          year: year.value,
          groupId: groupId.value,
        });
        if (searchCancelled) return;
        if (res.isSuccess && res.data) {
          if (res.data.code === 403 && res.data.isSuccess === false) {
            message.warning(
              res.data.msg || "今日类案检索使用次数已达上限，请明天再试",
            );
            searched.value = 1;
            loading.value = false;
            dailyCaseSearchLimited.value = true;
            return;
          }
          caseListData.value = (res.data.hits || []).map((item) => ({
            ...item,
            id: item.ah || item.id,
            tags: ["案情摘要", "认定事实", "本院认为", "裁判结果"],
          }));
        }
      } catch (err) {
        console.error("检索失败", err);
        caseListData.value = [];
      } finally {
        if (!searchCancelled) {
          hybridSearchFinished = true;
          checkBothFinished();
        }
      }
      return;
    }

    // 2. 类案检索有数据，直接恢复
    const result = JSON.parse(searchRecord.responseResult);
    if (result.hits) {
      caseListData.value = result.hits.map((item) => ({
        ...item,
        id: item.ah || item.id,
        tags: ["案情摘要", "认定事实", "本院认为", "裁判结果"],
      }));
    }

    // 3. 处理推荐问题（总有数据则直接设置，否则重新请求）
    const questionsRecord = Array.isArray(detail)
      ? detail.find((d) => d.operationName === "AI追问")
      : null;
    if (questionsRecord?.responseResult) {
      try {
        const qData = JSON.parse(questionsRecord.responseResult);
        if (qData.questions) {
          window.__pendingFollowUpQuestions = qData.questions;
        }
      } catch (e) {
        // ignore parse error
      }
    } else {
      // 无历史追问数据，重新请求
      GenerateFollowUpQuestions(keyword.value)
        .then((res) => {
          if (res.isSuccess && res.data?.questions) {
            window.__pendingFollowUpQuestions = res.data.questions;
            workbenchRef.value?.workbenchRightRef?.setRecommendQuestions?.(
              res.data.questions,
            );
          }
        })
        .catch((err) => {
          console.error("推荐问题获取失败", err);
        });
    }

    // 4. 判断是否需要重新生成案例分析报告
    if (reportResultEmpty) {
      // 报告为空，直接切换到结果页，但 startAnalyze 会触发生成流
      searched.value = 3;
      nextTick(() => {
        workbenchRef.value?.startAnalyze?.();
      });
    } else {
      // 报告有数据，预置 md 内容，直接展示结果页
      if (reportRecord.responseResult) {
        window.__preloadMd = reportRecord.responseResult;
      }
      searched.value = 3;
      nextTick(() => {
        workbenchRef.value?.startAnalyze?.();
      });
    }
  } catch (err) {
    console.error("从共享数据恢复失败", err);
  }
};

onMounted(() => {
  changeRecommend();
});

// 对外暴露：从历史记录恢复数据（供 kevinHome 的 activeComponentRef 调用）
defineExpose({
  restoreFromHistory(detail) {
    restoreFromCrossData(detail);
  },
});

/**
 * 开始检索
 */
const handleSearch = async (form) => {
  console.log("检索条件", form);

  keyword.value = form.keyword;
  year.value = form.year;
  courtName.value = form.court;

  // 关键词未变化 → 直接显示结果页，不发起请求
  if (keyword.value === previousKeyword) {
    searched.value = 3;
    return;
  }

  searched.value = 2;

  // 重置完成状态
  hybridSearchFinished = false;
  thinkStreamFinished = false;
  // console.log(groupId,'groupId')
  // 与 HybridSearch 同时发起推荐问题加载，存到全局供 WorkbenchRight 挂载后取用
  // console.log('发起推荐问题请求:', form.keyword)
  GenerateFollowUpQuestions(form.keyword)
    .then((res) => {
      // console.log('推荐问题返回:', res)
      if (res.isSuccess && res.data?.questions) {
        window.__pendingFollowUpQuestions = res.data.questions;
        // 如果 WorkbenchRight 已挂载，直接设置
        workbenchRef.value?.workbenchRightRef?.setRecommendQuestions?.(
          res.data.questions,
        );
      }
    })
    .catch((err) => {
      console.error("推荐问题获取失败", err);
    });
  try {
    groupId.value = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}-${Math.random().toString(36).slice(2, 6)}`;
    const res = await HybridSearch({
      query: form.keyword,
      courtName: form.court,
      year: form.year,
      groupId: groupId.value,
    });
    if (searchCancelled) return;
    console.log(res.isSuccess && res.data, res.data?.hits);
    if (res.isSuccess && res.data) {
      // 检查是否触发推理限制（data.code === 403）
      if (res.data.code === 403 && res.data.isSuccess === false) {
        message.warning(
          res.data.msg || "今日类案检索使用次数已达上限，请明天再试",
        );
        searched.value = 1;
        loading.value = false;
        dailyCaseSearchLimited.value = true;
        return;
      }
      console.log(res.isSuccess && res.data, res.data.hits);
      caseListData.value = (res.data.hits || []).map((item) => ({
        ...item,
        id: item.ah || item.id,
        tags: ["案情摘要", "认定事实", "本院认为", "裁判结果"],
      }));
      // console.log(caseListData)
    }
  } catch (err) {
    console.error("检索失败", err);
    caseListData.value = [];
  } finally {
    if (!searchCancelled) {
      hybridSearchFinished = true;
      checkBothFinished();
    }
  }
};
// 思考流完成回调
const onThinkFinished = () => {
  // 从 SearchLoadingPanel 获取深度思考内容
  if (loadingPanelRef.value) {
    thinkHtml.value = loadingPanelRef.value.thinkHtml || "";
    thinkStreaming.value = loadingPanelRef.value.thinkStreaming || false;
    rawThinkText.value = loadingPanelRef.value.rawThinkText || "";
  }
  thinkStreamFinished = true;
  checkBothFinished();
};
// 三段全部加载完成回调
const handleLoadFinish = () => {
  // console.log("全部流程加载完毕，切换为结果页面")
  searched.value = 3;
  // 切换后等待 MdFileRead 挂载，再调用 startAnalyzeStream
  nextTick(() => {
    workbenchRef.value?.startAnalyze?.();
  });
};
/**
 * 返回首页
 */
// const backHome = () => {

//   searched.value = false

// }

/**
 * AI优化
 */
const handleOptimize = () => {
  console.log("AI优化");
};
const modalOpen = ref(false);
/**
 * 上传附件
 */
const handleUpload = () => {
  // console.log("上传附件")
  modalOpen.value = true;
};

// 接收上传解析结果，填入关键词并触发检索
const handleUploadFile = async (data) => {
  keyword.value = data.content || "";

  // 关键词未变化 → 直接显示结果页，不发起请求
  if (keyword.value === previousKeyword) {
    searched.value = 3;
    return;
  }

  searched.value = 2;

  // 重置完成状态
  hybridSearchFinished = false;
  thinkStreamFinished = false;
  //  console.log(groupId,'groupId')
  // 与 HybridSearch 同时发起推荐问题加载，存到全局供 WorkbenchRight 挂载后取用
  console.log("发起推荐问题请求:", keyword.value);
  GenerateFollowUpQuestions(keyword.value)
    .then((res) => {
      console.log("推荐问题返回:", res);
      if (res.isSuccess && res.data?.questions) {
        window.__pendingFollowUpQuestions = res.data.questions;
        // 如果 WorkbenchRight 已挂载，直接设置
        workbenchRef.value?.workbenchRightRef?.setRecommendQuestions?.(
          res.data.questions,
        );
      }
    })
    .catch((err) => {
      console.error("推荐问题获取失败", err);
    });
  try {
    groupId.value = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}-${Math.random().toString(36).slice(2, 6)}`;
    const res = await HybridSearch({
      query: keyword.value,
      courtName: "全部法院",
      year: 0,
      groupId: groupId.value,
    });
    if (searchCancelled) return;
    if (res.isSuccess && res.data) {
      // 检查是否触发推理限制
      if (res.data.code === 403 && res.data.isSuccess === false) {
        message.warning(
          res.data.msg || "今日类案检索使用次数已达上限，请明天再试",
        );
        searched.value = 1;
        loading.value = false;
        dailyCaseSearchLimited.value = true;
        hybridSearchFinished = true;
        checkBothFinished();
        return;
      }
      caseListData.value = (res.data.hits || []).map((item) => ({
        ...item,
        id: item.ah || item.id,
        tags: ["案情摘要", "认定事实", "本院认为", "裁判结果"],
      }));
    }
  } catch (err) {
    console.error("检索失败", err);
    caseListData.value = [];
  } finally {
    if (!searchCancelled) {
      hybridSearchFinished = true;
      checkBothFinished();
    }
  }
};
/**
 * 查看详情
 */
const goDetail = async (item) => {
  keyword.value = item.questionContent;
  // console.log(item)

  // 关键词未变化 → 直接显示结果页，不发起请求
  if (keyword.value === previousKeyword) {
    searched.value = 3;
    return;
  }

  searched.value = 2;

  // 重置完成状态
  hybridSearchFinished = false;
  thinkStreamFinished = false;
  // console.log(groupId,'groupId')
  // 与 HybridSearch 同时发起推荐问题加载，存到全局供 WorkbenchRight 挂载后取用
  // console.log('发起推荐问题请求:', item.questionContent)
  GenerateFollowUpQuestions(item.questionContent)
    .then((res) => {
      // console.log('推荐问题返回:', res)
      if (res.isSuccess && res.data?.questions) {
        window.__pendingFollowUpQuestions = res.data.questions;
        // 如果 WorkbenchRight 已挂载，直接设置
        workbenchRef.value?.workbenchRightRef?.setRecommendQuestions?.(
          res.data.questions,
        );
      }
    })
    .catch((err) => {
      console.error("推荐问题获取失败", err);
    });
  try {
    groupId.value = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}-${Math.random().toString(36).slice(2, 6)}`;
    const res = await HybridSearch({
      query: item.questionContent,
      courtName: "全部法院",
      year: 0,
      groupId: groupId.value,
    });
    if (searchCancelled) return;
    console.log(res.isSuccess && res.data, res.data.hits);
    if (res.isSuccess && res.data) {
      // 检查是否触发推理限制
      if (res.data.code === 403 && res.data.isSuccess === false) {
        message.warning(
          res.data.msg || "今日类案检索使用次数已达上限，请明天再试",
        );
        searched.value = 1;
        loading.value = false;
        dailyCaseSearchLimited.value = true;
        hybridSearchFinished = true;
        checkBothFinished();
        return;
      }
      console.log(res.isSuccess && res.data, res.data.hits);
      caseListData.value = (res.data.hits || []).map((item) => ({
        ...item,
        id: item.ah || item.id,
        tags: ["案情摘要", "认定事实", "本院认为", "裁判结果"],
      }));
      // console.log(caseListData)
    }
  } catch (err) {
    console.error("检索失败", err);
    caseListData.value = [];
  } finally {
    if (!searchCancelled) {
      hybridSearchFinished = true;
      checkBothFinished();
    }
  }
};
/**
 * 查看详情
 */
const goDetails = async (item) => {
  if (!item?.caseNo) return;
  try {
    const res = await AnalyzeCase({ caseNumber: item.caseNo });
    if (res.isSuccess && res.data) {
      analyzeDetailData.value = res.data;
    }
  } catch (err) {
    console.error("AnalyzeCase 请求失败：", err);
  }
  searched.value = 4;
};

/**
 * 查看详情
 */
const goView = async (item) => {
  console.log(item, "item2");
  if (!item?.ah) return;
  currentAH.value = item.ah;
  currentDetailFavorited.value = !!item.isFavorited;
  loading.value = true;
  try {
    const res = await AnalyzeCase({ caseNumber: item.ah });
    if (res.isSuccess && res.data) {
      analyzeDetailData.value = res.data;
    }
  } catch (err) {
    console.error("AnalyzeCase 请求失败：", err);
  } finally {
    loading.value = false;
    searched.value = 4;
  }
};

/**
 * 换一批
 */
const changeRecommend = () => {
  // recommendList.value = [...recommendList.value]
  //   .sort(() => Math.random() - 0.5)
  GetRandonList().then((res) => {
    if (res.code === 200) {
      recommendList.value = res.data.map((item, index) => {
        const num = index + 1;
        return {
          ...item,
          icon:
            item.icon || require(`@/assets/casesearchImg/Background${num}.png`),
        };
      });
    }
  });
};

// 返回首页时取消所有正在进行的搜索请求
watch(searched, (val) => {
  if (val === 1) {
    searchCancelled = true;
    // 中断分析报告流式请求（SearchWorkbench 用 v-show 保持挂载，需主动 cancel）
    workbenchRef.value?.cancelAnalyze?.();
  } else {
    searchCancelled = false;
  }
});
</script>

<style scoped lang="less">
.legal-search-home {
  width: 100%;

  max-width: 1780px;

  margin: 0 auto;

  // padding: 0 20px 0px;
}

/* ======================= */

.header-home {
  margin-top: 24px;
}

.header-home h1 {
  font-size: 42px;

  font-weight: 700;

  color: #12213f;

  margin-bottom: 12px;
}

.header-home p {
  color: #64748f;

  font-size: 16px;

  line-height: 30px;
}

/* ======================= */

.icons-title {
  display: flex;

  align-items: center;

  margin-top: 16px;

  color: #64748f;

  font-size: 14px;
}

.icons {
  width: 20px;

  height: 20px;

  margin-right: 8px;
}

/* ======================= */

.recommend-section {
  margin-top: 30px;

  background: #fff;

  border-radius: 16px;

  border: 1px solid #dfe6f2;

  box-shadow: 0 5px 18px rgba(35, 68, 120, 0.05);

  padding: 22px;
}

.recommend-header {
  display: flex;

  justify-content: space-between;

  align-items: center;

  margin-bottom: 18px;
}

.left h2 {
  font-size: 18px;

  color: #12213f;

  font-weight: 700;
}

/* ======================= */

@media (max-width: 992px) {
  .header-home h1 {
    font-size: 34px;
  }
}

@media (max-width: 768px) {
  .legal-search-home {
    padding: 20px 20px 0 20px;
  }

  .header-home h1 {
    font-size: 28px;
  }

  .header-home p {
    font-size: 14px;
  }
}

/* 全局加载遮罩 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-card {
  background: #fff;
  border-radius: 16px;
  padding: 32px 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}
.loading-text {
  font-size: 16px;
  color: #31425e;
  font-weight: 500;
}
</style>
